import type { Outcome, VisitInput } from "@little-finds/core";

export type DraftObservation = {
  product_id: string;
  outcome: Outcome | "";
  variant: string;
};
export type SightingDraft = {
  request_id: string;
  store: string;
  observations: DraftObservation[];
  date: string;
  note: string;
  photo_path?: string;
  photo_name?: string;
  pending_visit?: VisitInput;
};
const outcomes: Outcome[] = ["saw_it", "couldnt_find", "staff_unavailable"];
export const draftKeyFor = (owner: string, demo = false) =>
  demo
    ? "little-finds:demo:draft"
    : `little-finds:draft:${encodeURIComponent(owner)}`;

/** Accept older drafts without losing their per-visit observations. */
export function restoreDraft(
  raw: string | null,
  requestId: string,
  now = new Date(),
): SightingDraft {
  const empty: SightingDraft = {
    request_id: requestId,
    store: "",
    observations: [],
    note: "",
    date: new Date(now.getTime() - now.getTimezoneOffset() * 60000)
      .toISOString()
      .slice(0, 16),
  };
  if (!raw) return empty;
  try {
    const v = JSON.parse(raw);
    const observations = Array.isArray(v.observations)
      ? v.observations
      : Array.isArray(v.ids)
        ? v.ids.map((id: string) => ({
            product_id: id,
            outcome: v.outcome,
            variant: v.variant,
          }))
        : [];
    return {
      request_id: typeof v.request_id === "string" ? v.request_id : requestId,
      store: typeof v.store === "string" ? v.store : "",
      observations: observations
        .filter(
          (o: DraftObservation, i: number, all: DraftObservation[]) =>
            typeof o?.product_id === "string" &&
            all.findIndex((p) => p.product_id === o.product_id) === i,
        )
        .slice(0, 20)
        .map((o: DraftObservation) => ({
          product_id: o.product_id,
          outcome: outcomes.includes(o.outcome as Outcome) ? o.outcome : "",
          variant: typeof o.variant === "string" ? o.variant.slice(0, 100) : "",
        })),
      date:
        typeof v.date === "string" &&
        Number.isFinite(new Date(v.date).getTime())
          ? v.date
          : "",
      note: typeof v.note === "string" ? v.note.slice(0, 280) : "",
      photo_path: typeof v.photo_path === "string" ? v.photo_path : undefined,
      photo_name: typeof v.photo_name === "string" ? v.photo_name : undefined,
      pending_visit:
        v.pending_visit &&
        typeof v.pending_visit === "object" &&
        typeof v.pending_visit.request_id === "string" &&
        v.pending_visit.request_id === v.request_id &&
        typeof v.pending_visit.store_id === "string" &&
        Number.isFinite(Date.parse(v.pending_visit.observed_at)) &&
        Array.isArray(v.pending_visit.observations) &&
        v.pending_visit.observations.length > 0 &&
        v.pending_visit.observations.length <= 20 &&
        v.pending_visit.observations.every(
          (o: DraftObservation) =>
            typeof o?.product_id === "string" &&
            outcomes.includes(o.outcome as Outcome),
        )
          ? v.pending_visit
          : undefined,
    };
  } catch {
    return empty;
  }
}

export function draftVisit(draft: SightingDraft, now = Date.now()): VisitInput {
  if (!draft.store || !draft.observations.length)
    throw new Error("Choose a shop and at least one product.");
  if (draft.observations.length > 20)
    throw new Error("Choose up to 20 products for one visit.");
  if (draft.observations.some((o) => !outcomes.includes(o.outcome as Outcome)))
    throw new Error("Choose what happened for each selected product.");
  const at = new Date(draft.date);
  if (
    !Number.isFinite(at.getTime()) ||
    at.getTime() > now + 300000 ||
    at.getTime() < now - 7 * 86400000
  )
    throw new Error("Choose a valid visit time within the past 7 days.");
  return {
    request_id: draft.request_id,
    store_id: draft.store,
    observed_at: at.toISOString(),
    observations: draft.observations.map((o) => ({
      product_id: o.product_id,
      outcome: o.outcome as Outcome,
      variant: o.variant.trim() || undefined,
    })),
    note: draft.note.trim() || undefined,
    photo_path: draft.photo_path,
  };
}

function photoDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const open = indexedDB.open("little-finds-drafts", 1);
    open.onupgradeneeded = () => open.result.createObjectStore("photos");
    open.onsuccess = () => resolve(open.result);
    open.onerror = () =>
      reject(open.error ?? new Error("Local photo storage is unavailable."));
  });
}
export async function readDraftPhoto(key: string): Promise<File | null> {
  const db = await photoDb();
  try {
    return await new Promise((resolve, reject) => {
      const tx = db.transaction("photos", "readonly"),
        req = tx.objectStore("photos").get(key);
      req.onsuccess = () => resolve(req.result ?? null);
      req.onerror = () => reject(req.error);
    });
  } finally {
    db.close();
  }
}
export async function saveDraftPhoto(
  key: string,
  photo: File | null,
): Promise<void> {
  const db = await photoDb();
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction("photos", "readwrite");
      if (photo) tx.objectStore("photos").put(photo, key);
      else tx.objectStore("photos").delete(key);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error);
    });
  } finally {
    db.close();
  }
}
